import { useMemo, useContext, useCallback, JSX } from "react"
import { useDispatch } from "react-redux"
import { DeepReadonly } from "ts-essentials"
import { includes } from "lodash"
import { IconButton, IconButtonOwnProps } from "@mui/material"
import { OfflineBolt, OpenInNew } from "@mui/icons-material"
import Tooltip from "components/material/tooltip"
import SelectedPosition from "components/selected-position-2"
import { useModelMetadata } from "resources/models"
import { isCompositeBase as isCompositeBaseHelper, getCompositeSecurityTooltip } from "helpers/compositeSecurities"
import { LOADING, ERROR } from "helpers/data-statuses"
import { InstrumentMetadata } from "helpers/types/instruments/metadata"
import { IsoDate } from "helpers/types/dates"
import Loading from "components/loading"
import Error from "components/error"
import { Creators as CompositeCreators } from "modules/analyze-composite-security/model"
import { IDashboardContext } from "connected-components/dashboard/context/types"

import "./styles.scss"

export default function InstrumentSelector({
  context,
  icon,
  labelPrefix,
  size = "small",
  selectedInstrumentMetadata,
  lightningBoltMessage,
  startDate,
  endDate,
  onClear,
  openSearch,
}: DeepReadonly<InstrumentSelectorProps>) {
  const dispatch = useDispatch()
  const { modelId } = useContext(context)
  const [modelMetadata, modelMetadataStatus] = useModelMetadata(modelId)

  const modelSupportsComposites = useMemo(
    () => !includes(modelMetadata?.missingDataFiles, "composite"),
    [modelMetadata],
  )

  const handleOpenCompositeSecurity = useCallback(
    (composite: InstrumentMetadata) => dispatch(CompositeCreators.openCompositeSecurity(composite)),
    [dispatch],
  )

  const openCompositeContent = useMemo(() => {
    if (!selectedInstrumentMetadata || !isCompositeBaseHelper(selectedInstrumentMetadata)) return null

    const isETFWithMissingConstituents = selectedInstrumentMetadata.type === "ETF" && (!modelSupportsComposites || !selectedInstrumentMetadata.hasComposition)
    const analyzeCompositeAvailable = !isETFWithMissingConstituents || ["BASKET", "INDEX"].includes(selectedInstrumentMetadata.type)

    return <Tooltip title={getCompositeSecurityTooltip(selectedInstrumentMetadata, { modelSupportsComposites })}>
      <div className="composite-icon-wrapper">
        <IconButton
          onClick={() => handleOpenCompositeSecurity(selectedInstrumentMetadata)}
          disabled={!analyzeCompositeAvailable}
          size="small"
          className="composite-icon-button"
        >
          <OpenInNew fontSize="small" />
        </IconButton>
      </div>
    </Tooltip>
  }, [selectedInstrumentMetadata, modelSupportsComposites, handleOpenCompositeSecurity])

  if (modelMetadataStatus === LOADING) {
    return <div className="connected_components_instrument-selector-status"><Loading /></div>
  }

  if (modelMetadataStatus === ERROR) {
    return <div className="connected_components_instrument-selector-status"><Error /></div>
  }

  return <div className="connected_components_instrument-selector">
    {lightningBoltMessage && (
      <Tooltip title={lightningBoltMessage}>
        <OfflineBolt className="warning" />
      </Tooltip>
    )}
    {icon}
    <SelectedPosition
      labelPrefix={labelPrefix}
      instrumentMetadata={selectedInstrumentMetadata}
      onIconClick={onClear}
      iconClassName="clear-button"
      onClick={startDate && endDate ? undefined : openSearch}
      size={size}
      startDate={startDate}
      endDate={endDate}
    />
    {openCompositeContent}
  </div>
}

interface InstrumentSelectorProps {
  context: IDashboardContext
  openSearch: () => void
  selectedInstrumentMetadata: InstrumentMetadata
  icon?: JSX.Element
  labelPrefix?: string
  size?: IconButtonOwnProps["size"]
  lightningBoltMessage?: string
  startDate?: IsoDate
  endDate?: IsoDate
  onClear: () => void
}
